<template lang="pug">
.collections-block-editor
  .editor-header
    h3 {{ isCreating ? 'New Collections' : 'Collections' }}

  .editor-form
    el-form(:model="formData" label-position="top" :rules="rules" ref="formRef")
      el-form-item(
        label="Block Title"
        prop="title"
        :rules="{ required: true, message: 'Block title is required', trigger: 'blur' }"
      )
        el-input(
          v-model="formData.title"
          placeholder="Enter block title"
          size="default"
        )

      .collections-groups-section
        h4 Collections Groups
        .collections-groups-list(v-if="formData.groups.length > 0")
          .reorder-hint(v-if="formData.groups.length > 1")
            span Drag to reorder collection groups
          draggable.collections-groups-draggable(
            v-model="formData.groups"
            handle=".drag-handle"
            ghost-class="ghost-collection-group"
            item-key="id"
            @start="dragStart"
            @end="dragEnd"
          )
            template(#item="{ element: group, index }")
              .collection-group(:key="group.id")
                .collection-group-header
                  .drag-handle.cursor-move
                    fai(type="fas" icon="grip-vertical").size-14
                  h5 Collection {{ index + 1 }}
                  .collection-group-actions
                    el-button(
                      :type="group.isEnabled ? 'primary' : 'info'"
                      size="small"
                      text
                      @click="toggleGroupVisibility(index)"
                      title="Toggle visibility"
                    ).has-fai
                      fai(:type="group.isEnabled ? 'fal' : 'fas'" icon="eye").size-14
                    el-button(
                      type="danger"
                      size="small"
                      text
                      @click="removeGroup(index)"
                    ).has-fai
                      fai(type="fal" icon="trash").size-14

                el-form-item(
                  label="Title"
                  :prop="`groups.${index}.title`"
                  :rules="{ required: true, message: 'Title is required', trigger: 'blur' }"
                )
                  el-input(
                    v-model="group.title"
                    placeholder="Enter collection title"
                    size="default"
                  )

                el-form-item(
                  label="Cover Image"
                  :prop="`groups.${index}.coverImageUrl`"
                  :rules="{ required: true, message: 'Cover image is required', trigger: 'change' }"
                )
                  .image-upload-container
                    .image-preview(v-if="group.coverImageUrl")
                      img(:src="group.coverImageUrl" alt="Collection cover image preview")
                      .image-actions
                        el-button(type="primary" size="small"
                          @click="triggerFileInput(index)") Change
                    el-upload(
                      ref="uploadRef"
                      drag
                      :show-file-list="false"
                      :before-upload="beforeUpload"
                      :http-request="(payload) => handleImageUpload(payload, index)"
                      v-loading="uploading.image && formData.currentImageIndex === index"
                      action=""
                      accept="image/*"
                      :class="{'img-uploader': true, 'img-uploader--hidden': group.coverImageUrl, 'pos-r': true, 'mb-0': true}"
                    )
                      template(v-if="!group.coverImageUrl")
                        img(src="@/assets/icons/file-image-outline.svg").img-uploader-icon
                        p.upload-picture-text Drop an image file here or
                        div.fake-upload-button Browse For File

                el-form-item(
                  :prop="`groups.${index}.organizationsList`"
                  :rules="{ required: true, message: 'At least one organization is required', trigger: 'change' }"
                )
                  template(#label)
                    .organizations-label-row
                      span Organizations
                      el-button(
                        type="primary"
                        size="small"
                        @click="showOrganizationSelector(index)"
                        plain
                      ) Add Organizations

                  .organizations-list
                    .organizations-list-content(v-if="group.organizationsList.length > 0")
                      .reorder-hint(v-if="group.organizationsList.length > 1")
                        span Drag to reorder organizations
                      draggable.selected-organizations-list(
                        v-model="group.organizationsList"
                        handle=".drag-handle"
                        ghost-class="ghost-organization"
                        item-key="id"
                        @start="dragStart"
                        @end="dragEnd"
                      )
                        template(#item="{ element: organizationId, index: orgIndex }")
                          .selected-organization-item(:key="organizationId")
                            .selected-organization-content
                              .drag-handle.cursor-move
                                fai(type="fas" icon="grip-vertical").size-14
                              .selected-organization-thumbnail
                                img(
                                  v-if="getOrganizationAvatar(organizationId)"
                                  :src="getOrganizationAvatar(organizationId)"
                                )
                                .no-image(v-else)
                              .selected-organization-info
                                .selected-organization-title
                                  | {{ getOrganizationName(organizationId) }}
                            .selected-organization-actions
                              el-button(
                                type="danger"
                                size="small"
                                text
                                @click="removeOrganizationFromGroup(index, orgIndex)"
                              ).has-fai
                                fai(type="fal" icon="trash").size-14

                    .organizations-list-empty(v-else)
                      span No organizations selected. Click "Add Organizations"
                      | to select organizations.

                .group-divider(v-if="index < formData.groups.length - 1")

        .no-groups(v-else)
          p No collection groups added yet. Add a group below.

        .add-group-button
          el-button(
            type="primary"
            icon="el-icon-plus"
            @click="addGroup"
            size="default"
          ) Add Collection Group

      .editor-actions
        el-button(
          @click="handleCancel"
          size="default"
        ) Cancel
        el-button(
          type="primary"
          @click="handleSave"
          size="default"
          :loading="saving"
        ) Save

  // Organization selector dialog
  DialogSelectOrganizations(
    v-model="showOrgSelector"
    :preSelectedOrganizations="currentGroupOrganizations"
    @confirm="handleOrganizationsSelected"
  )
</template>

<script>
import {
  defineComponent, ref, reactive, computed,
} from 'vue';
import draggable from 'vuedraggable';
import { ElMessage } from 'element-plus';
import useStaticPageUtils from '../shared/index.ts';
import fileUpload from '@/shared/upload';
import { resizeAndConvertToPNG } from '@/shared/utility-functions';
import DialogSelectOrganizations from './DialogSelectOrganizations.vue';
import fai from '@/plugins/font-awesome';

export default defineComponent({
  name: 'CollectionsBlock',
  components: { fai, draggable, DialogSelectOrganizations },
  props: {
    block: {
      type: Object,
      default: null,
    },
    isCreating: {
      type: Boolean,
      default: false,
    },
    pageId: {
      type: String,
      required: true,
    },
    staticPageData: {
      type: Object,
      default: null,
    },
  },
  emits: ['save', 'cancel'],
  setup(props, { emit }) {
    const { createOrUpdateCollectionsBlock } = useStaticPageUtils();
    const formRef = ref(null);
    const uploadRef = ref([]);
    const saving = ref(false);
    const { uploadStatus, beforeUpload, handleUploadRequest } = fileUpload('stickers', 'Collections');
    const isUploading = reactive({
      image: false,
    });
    const showOrgSelector = ref(false);
    const currentGroupIndex = ref(-1);

    // Store organizations selected from dialog that might not be in staticPageData yet
    const dialogSelectedOrganizations = ref([]);

    // Initialize form data with existing values or defaults
    const formData = reactive({
      title: props.block?.details?.title || '',
      groups: props.block?.details?.groups?.map((group, index) => ({
        id: group.id || `group-${Date.now()}-${index}`,
        title: group.title || '',
        coverImageUrl: group.coverImageUrl || '',
        organizationsList: group.organizationsList || [],
        isEnabled: group.isEnabled !== undefined ? group.isEnabled : true,
      })) || [],
      currentImageIndex: -1,
    });

    // Form validation rules
    const rules = {
      groups: {
        type: 'array',
        required: true,
        message: 'Please add at least one collection group',
        trigger: 'change',
        validator: (rule, value) => value.length > 0,
      },
    };

    // Combine organizations from static page data and dialog selections
    const organizations = computed(() => {
      const staticPageOrganizations = props.staticPageData?.organizations || [];
      const allOrganizations = [...staticPageOrganizations];

      // Add organizations from dialog that aren't already in staticPageData
      dialogSelectedOrganizations.value.forEach((dialogOrganization) => {
        if (!allOrganizations.find((o) => o.id === dialogOrganization.id)) {
          allOrganizations.push(dialogOrganization);
        }
      });

      return allOrganizations;
    });

    // Computed property for current group organizations
    const currentGroupOrganizations = computed(() => {
      if (currentGroupIndex.value >= 0 && formData.groups[currentGroupIndex.value]) {
        return formData.groups[currentGroupIndex.value].organizationsList;
      }
      return [];
    });

    // Get organization name by ID
    function getOrganizationName(organizationId) {
      const organization = organizations.value.find((o) => o.id === organizationId);
      return organization ? organization.name : '';
    }

    // Get organization avatar by ID
    function getOrganizationAvatar(organizationId) {
      const organization = organizations.value.find((o) => o.id === organizationId);
      return organization ? organization.avatar : null;
    }

    // Add a new empty group
    function addGroup() {
      formData.groups.push({
        id: `group-${Date.now()}-${formData.groups.length}`,
        title: '',
        coverImageUrl: '',
        organizationsList: [],
        isEnabled: true,
      });
      // Set the current index to the newly added group
      formData.currentImageIndex = formData.groups.length - 1;
    }

    // Remove a group by index
    function removeGroup(index) {
      formData.groups.splice(index, 1);
    }

    // Toggle group visibility
    function toggleGroupVisibility(index) {
      formData.groups[index].isEnabled = !formData.groups[index].isEnabled;
    }

    // Remove organization from specific group
    function removeOrganizationFromGroup(groupIndex, orgIndex) {
      const removedOrganizationId = formData.groups[groupIndex].organizationsList[orgIndex];
      formData.groups[groupIndex].organizationsList.splice(orgIndex, 1);

      // Also remove from dialog selected organizations if it exists there and is not used elsewhere
      const isUsedElsewhere = formData.groups.some((group, gIndex) => (
        gIndex !== groupIndex && group.organizationsList.includes(removedOrganizationId)
      ));

      if (!isUsedElsewhere) {
        const dialogIndex = dialogSelectedOrganizations.value.findIndex(
          (o) => o.id === removedOrganizationId,
        );
        if (dialogIndex !== -1) {
          dialogSelectedOrganizations.value.splice(dialogIndex, 1);
        }
      }
    }

    // Show organization selector for a specific group
    function showOrganizationSelector(index) {
      currentGroupIndex.value = index;
      showOrgSelector.value = true;
    }

    // Handle organizations selected from dialog
    function handleOrganizationsSelected(selectedIds, selectedOrganizationObjects = []) {
      if (currentGroupIndex.value >= 0) {
        formData.groups[currentGroupIndex.value].organizationsList = selectedIds;
        // Store the full organization objects for display
        dialogSelectedOrganizations.value = selectedOrganizationObjects;
      }
      currentGroupIndex.value = -1;
    }

    // Handle image upload
    async function handleImageUpload(payload, index) {
      try {
        isUploading.image = true;
        formData.currentImageIndex = index;

        // Process image before upload
        let uploadPayload = payload;
        if (payload.file && payload.file.type.startsWith('image/')) {
          try {
            const processedImage = await resizeAndConvertToPNG(payload.file, 1200, 600, 0.9);
            uploadPayload = { ...payload, file: processedImage };
          } catch (processingError) {
            console.error('Error processing image:', processingError);
            // Continue with original file if processing fails
          }
        }

        // Upload the image
        await handleUploadRequest(uploadPayload);

        if (uploadStatus.status === 'ok') {
          // Use the resourceUrl from the server instead of blob URL
          formData.groups[index].coverImageUrl = uploadStatus.resourceUrl;
          ElMessage.success('Image uploaded successfully');
        } else {
          ElMessage.error(uploadStatus.message || 'Failed to upload image');
        }
      } catch (error) {
        console.error('Error uploading collection cover image:', error);
        ElMessage.error('Failed to upload image');
      } finally {
        isUploading.image = false;
      }
    }

    // Trigger file input click when Change button is clicked
    function triggerFileInput(index) {
      // Set current index for upload
      formData.currentImageIndex = index;

      // Find the upload input in the current group and trigger click
      if (uploadRef.value && uploadRef.value[index]) {
        const uploadInput = uploadRef.value[index].$el.querySelector('input[type="file"]');
        if (uploadInput) {
          uploadInput.click();
        }
      }
    }

    // Drag event handlers
    function dragStart() {
      // Drag started
    }

    function dragEnd() {
      console.log('Drag ended');
    }

    // Handle save
    async function handleSave() {
      if (!formRef.value) return;

      try {
        await formRef.value.validate();
        saving.value = true;

        const input = {
          title: formData.title,
          groups: formData.groups.map((group) => ({
            title: group.title,
            coverImageUrl: group.coverImageUrl,
            organizationsList: group.organizationsList,
            isEnabled: group.isEnabled,
          })),
          sortOrder: props.isCreating ? null : (props.block?.sortOrder || 0),
          parentId: props.pageId,
          id: props.isCreating ? null : props.block?.id,
        };

        const result = await createOrUpdateCollectionsBlock(input, props.staticPageData);

        ElMessage.success(props.isCreating ? 'Collections block created successfully' : 'Collections block updated successfully');
        emit('save', result);
      } catch (error) {
        console.error('Error saving collections block:', error);
        ElMessage.error('Failed to save collections block');
      } finally {
        saving.value = false;
      }
    }

    // Handle cancel
    function handleCancel() {
      emit('cancel');
    }

    return {
      formRef,
      uploadRef,
      formData,
      rules,
      saving,
      uploading: isUploading,
      showOrgSelector,
      currentGroupOrganizations,
      dialogSelectedOrganizations,
      getOrganizationName,
      getOrganizationAvatar,
      addGroup,
      removeGroup,
      toggleGroupVisibility,
      removeOrganizationFromGroup,
      showOrganizationSelector,
      handleOrganizationsSelected,
      handleSave,
      handleCancel,
      handleImageUpload,
      beforeUpload,
      triggerFileInput,
      dragStart,
      dragEnd,
    };
  },
});
</script>

<style scoped>
.collections-block-editor {
  width: 100%;
}

.editor-form {
  max-width: 700px;
  margin-top: 20px;
}

.collections-groups-section {
  margin-top: 24px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 16px;
  background-color: #f9f9f9;
}

.collections-groups-list {
  margin-top: 16px;
}

.collection-group {
  background-color: #fff;
  border-radius: 4px;
  padding: 16px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.collection-group-header {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 16px;
  gap: 12px;
}

.collection-group-header h5 {
  flex: 1;
  margin: 0;
  font-size: 16px;
  font-weight: 500;
}

.drag-handle {
  cursor: move;
  cursor: grab;
  color: #909399;
  margin-right: 4px;
  display: flex;
  align-items: center;
  padding: 8px;
  background-color: #f5f7fa;
  border-radius: 4px;
  transition: all 0.3s;
}

.drag-handle:hover {
  color: #1EBEAF;
  background-color: #e6f7f6;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.drag-handle:active {
  cursor: grabbing;
}

.ghost-collection-group {
  opacity: 0.5;
  background: #e6f7f6;
  border: 1px dashed #1EBEAF;
}

.reorder-hint {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #909399;
  margin-bottom: 12px;
}

.collections-groups-draggable {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.group-divider {
  height: 1px;
  background-color: #ebeef5;
  margin: 24px 0;
}

.image-upload-container {
  width: 100%;
}

.image-preview {
  position: relative;
  margin-bottom: 0;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  overflow: hidden;
  max-width: 300px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

.image-preview img {
  width: 100%;
  height: auto;
  display: block;
}

.image-preview .image-actions {
  position: absolute;
  bottom: 0;
  right: 0;
  padding: 8px;
  background-color: rgba(0, 0, 0, 0.5);
  border-top-left-radius: 4px;
}

.img-uploader {
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  text-align: center;
  transition: border-color 0.3s;
}

.img-uploader:hover {
  border-color: #1EBEAF;
}

.img-uploader--hidden {
  height: 0;
  padding: 0;
  margin: 0;
  border: none;
  overflow: hidden;
  opacity: 0;
}

.img-uploader-icon {
  width: 40px;
  height: 40px;
  margin-bottom: 8px;
}

.upload-picture-text {
  color: #606266;
  font-size: 14px;
  margin: 8px 0;
}

.fake-upload-button {
  color: #1EBEAF;
  font-size: 14px;
  font-weight: 500;
  margin-top: 8px;
  cursor: pointer;
}

.organizations-label-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.organizations-list {
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 12px;
}

.organizations-list-content {
  max-height: 300px;
  overflow-y: auto;
}

.selected-organizations-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.selected-organization-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  background-color: #f9f9f9;
}

.selected-organization-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0; /* Allow flex items to shrink below their content size */
}

.selected-organization-thumbnail {
  width: 40px;
  height: 40px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;
}

.selected-organization-thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.no-image {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #e0e0e0;
  color: #606266;
  font-size: 12px;
  border: 1px solid #d4d4d4;
}

.selected-organization-info {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  max-width: calc(100% - 120px); /* Reserve space for actions */
}

.selected-organization-title {
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.selected-organization-actions {
  display: flex;
  align-items: center;
  flex-shrink: 0; /* Prevent actions from shrinking */
  margin-left: 8px;
}

.ghost-organization {
  opacity: 0.5;
  background: #e6f7f6;
  border: 1px dashed #1EBEAF;
}

.organizations-list-empty {
  padding: 20px 0;
  text-align: center;
  color: #909399;
}

.no-groups {
  text-align: center;
  padding: 24px;
  color: #909399;
}

.add-group-button {
  margin-top: 16px;
  text-align: center;
}

.editor-actions {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #ebeef5;
  display: flex;
  gap: 12px;
  justify-content: flex-end;
}
</style>
